import React, { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Save, Plus, Trash2, Loader2 } from 'lucide-react'
import { showSaveSuccess, showSaveError, showValidationError, showLoading, closeSwal, showError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { quotationsService } from '@/services/quotationsService'
import { financeService } from '@/services/financeService'
import { 
  Quotation, 
  QuotationLine, 
  FinanceCustomer, 
  Project, 
  TaxRate, 
  PaymentTerm 
} from '@/api/types'

/**
 * Quotation Detail/Edit Page
 * Form for creating and editing quotations
 */
export const QuotationsDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuthStore()
  
  const isEditMode = id !== 'new' && id !== undefined
  const branchId = user?.selectedBranch?.branchId
  const companyId = 0 // TODO: Get from user/branch if available
  // Get customerId from route state if available
  const customerIdFromState = (location.state as any)?.customerId

  // Helper function to find 15% VAT tax rate (prioritize taxRateId: 1)
  const getDefaultTaxRate = (taxRates: TaxRate[]): { taxRateId: number; taxRate: number } | null => {
    // First, try to find taxRateId === 1
    const taxById = taxRates.find(t => t.taxRateId === 1)
    if (taxById) {
      return {
        taxRateId: taxById.taxRateId,
        taxRate: taxById.taxPercent || taxById.taxRate || 0
      }
    }
    // Fallback: find by 15% rate
    const tax15 = taxRates.find(t => {
      const rate = t.taxPercent || t.taxRate || 0
      return Math.abs(rate - 15) < 0.01 // Allow small floating point differences
    })
    if (tax15) {
      return {
        taxRateId: tax15.taxRateId,
        taxRate: tax15.taxPercent || tax15.taxRate || 0
      }
    }
    return null
  }

  // Initialize default rows for new quotations
  const getDefaultRows = (taxRates: TaxRate[] = []): QuotationLine[] => {
    const defaultTax = getDefaultTaxRate(taxRates)
    return Array.from({ length: 3 }, (): QuotationLine => ({
      lineId: 0,
      quotationId: 0,
      itemId: 0,
      description: '',
      qty: 0,
      unitPrice: 0,
      itemPrice: 0,
      discountPercent: 0,
      discountAmount: 0,
      taxableAmount: 0,
      taxRateId: defaultTax?.taxRateId || 0,
      taxRate: defaultTax?.taxRate || 0,
      taxPercent: defaultTax?.taxRate || 0,
      taxAmount: 0,
      lineAmount: 0,
      companyId: companyId,
      branchId: branchId || 0,
    }))
  }

  // Form state
  const [quotation, setQuotation] = useState<Quotation>({
    quotationId: 0,
    quotationDate: new Date().toISOString().split('T')[0],
    quotationNumber: '',
    customerId: 0,
    dueDate: '',
    projectId: 0,
    paymentTermId: 0,
    poNumber: '',
    branchInvoice: false,
    subTotal: 0,
    totalTax: 0,
    totalDiscount: 0,
    total: 0,
    note: '',
    companyId: companyId,
    branchId: branchId || user?.selectedBranch?.branchId || 0, // Ensure branchId is never null
    retentionPercent: 0,
    totalRetentionAmount: 0,
    quotationD: getDefaultRows([]),
  })

  // Lookup data
  const [customers, setCustomers] = useState<FinanceCustomer[]>([])
  const [selectedCustomer, setSelectedCustomer] = useState<FinanceCustomer | null>(null)
  const [projects, setProjects] = useState<Project[]>([])
  const [taxRates, setTaxRates] = useState<TaxRate[]>([])
  const [paymentTerms, setPaymentTerms] = useState<PaymentTerm[]>([])

  // UI state
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deletedRowIndices, setDeletedRowIndices] = useState<Set<number>>(new Set())
  const [isPosted, setIsPosted] = useState(false) // Track if quotation is posted

  // Load lookup data
  useEffect(() => {
    if (!branchId) return

    const loadLookups = async () => {
      try {
        const [customersData, taxRatesData, paymentTermsData] = await Promise.all([
          financeService.getAllCustomers(branchId),
          financeService.getTaxRates(branchId),
          financeService.getPaymentTerms(),
        ])
        setCustomers(customersData)
        setTaxRates(taxRatesData)
        setPaymentTerms(paymentTermsData)
      } catch (error: any) {
        console.error('Error loading lookup data:', error)
        await showError('Error', 'Failed to load lookup data')
      }
    }

    loadLookups()
  }, [branchId])

  // Set default VAT (15%) for rows that don't have taxRateId when taxRates are loaded
  useEffect(() => {
    if (taxRates.length === 0) return
    
    const defaultTax = getDefaultTaxRate(taxRates)
    if (!defaultTax) {
      console.warn('Default tax rate (15% or ID 1) not found in taxRates:', taxRates)
      return
    }

    console.log('Setting default VAT:', defaultTax)

    setQuotation(prev => {
      const hasRowsWithoutVAT = prev.quotationD?.some(line => !line.taxRateId || line.taxRateId === 0)
      if (!hasRowsWithoutVAT) return prev

      const updatedLines = prev.quotationD?.map(line => {
        // Only set default VAT if taxRateId is 0 or not set
        if (!line.taxRateId || line.taxRateId === 0) {
          console.log('Setting default VAT for line:', line, 'to taxRateId:', defaultTax.taxRateId)
          return {
            ...line,
            taxRateId: defaultTax.taxRateId,
            taxRate: defaultTax.taxRate,
            taxPercent: defaultTax.taxRate,
          }
        }
        return line
      }) || []

      return { ...prev, quotationD: updatedLines }
    })
  }, [taxRates])

  // Load quotation data for edit (wait for customers and taxRates to load first)
  useEffect(() => {
    if (!isEditMode || !branchId || !id || customers.length === 0 || taxRates.length === 0) return

    const loadQuotation = async () => {
      try {
        setLoading(true)
        let quotationData: Quotation | null = null
        let linesData: QuotationLine[] = []
        const quotationId = Number(id)

        // Try to get quotation by ID first
        try {
          quotationData = await quotationsService.getQuotationById(branchId, quotationId)
        } catch (error: any) {
          console.warn('GetQuotationById failed, trying GetQuotationByCustomer:', error)
          // If GetQuotationById fails and we have customerId, try GetQuotationByCustomer
          if (customerIdFromState) {
            try {
              quotationData = await quotationsService.getQuotationByCustomerId(branchId, customerIdFromState)
              // Verify that the quotation ID matches
              if (quotationData.quotationId !== quotationId) {
                console.warn('Quotation ID mismatch. Expected:', quotationId, 'Got:', quotationData.quotationId)
                // Still use it if it's the only option
              }
            } catch (customerError: any) {
              console.error('GetQuotationByCustomerId also failed:', customerError)
              throw new Error('Failed to load quotation. Both GetQuotationById and GetQuotationByCustomer failed.')
            }
          } else {
            // Re-throw the original error if we don't have customerId
            throw error
          }
        }

        // Get quotation lines
        try {
          linesData = await quotationsService.getQuotationLinesById(branchId, quotationId)
        } catch (error: any) {
          console.warn('Failed to load quotation lines:', error)
          // Continue with empty lines if lines fail to load
          linesData = []
        }

        // Ensure branchId is always set from current selected branch, never null
        const currentBranchIdForLoad = branchId || user?.selectedBranch?.branchId
        if (quotationData) {
          console.log('Loaded quotation data:', quotationData)
          console.log('Loaded lines data:', linesData)
          
          // Check if quotation is posted
          const posted = (quotationData as any).isPosted === true || (quotationData as any).isPosted === 1
          setIsPosted(posted)
          
          if (posted) {
            await showError('Cannot Edit', 'Quotation is already converted to invoice and cannot be edited')
            // Navigate back after a short delay
            setTimeout(() => {
              navigate('/quotations')
            }, 2000)
            return
          }
          
          // Ensure all fields are properly set
          const updatedQuotation: Quotation = {
            ...quotationData,
            branchId: currentBranchIdForLoad || quotationData.branchId || 0, // Ensure branchId is never null
            quotationD: linesData && linesData.length > 0 ? linesData : getDefaultRows(taxRates),
            // Ensure date is in correct format
            quotationDate: quotationData.quotationDate || new Date().toISOString().split('T')[0],
            dueDate: quotationData.dueDate || '',
            // Ensure numeric fields have defaults
            projectId: quotationData.projectId || 0,
            paymentTermId: quotationData.paymentTermId || 0,
            poNumber: quotationData.poNumber || '',
            branchInvoice: quotationData.branchInvoice !== undefined ? quotationData.branchInvoice : false,
            retentionPercent: (quotationData as any).retentionPercent || 0,
            totalRetentionAmount: (quotationData as any).totalRetentionAmount || 0,
          }
          
          console.log('Setting quotation state:', updatedQuotation)
          setQuotation(updatedQuotation)

          // Set selected customer
          if (quotationData.customerId) {
            const customer = customers.find(c => c.customerId === quotationData.customerId)
            if (customer) {
              console.log('Setting selected customer:', customer)
              setSelectedCustomer(customer)
              // Load projects for this customer
              loadProjects(customer.customerId)
            } else {
              console.warn('Customer not found for customerId:', quotationData.customerId)
            }
          }
        } else {
          throw new Error('Quotation data is null')
        }
      } catch (error: any) {
        console.error('Error loading quotation:', error)
        await showError('Error', error.message || 'Failed to load quotation')
      } finally {
        setLoading(false)
      }
    }

    loadQuotation()
  }, [isEditMode, branchId, id, customers.length, taxRates.length, customerIdFromState])

  // Load projects when customer changes
  const loadProjects = async (customerId: number) => {
    if (!branchId || !customerId) {
      setProjects([])
      return
    }

    try {
      console.log('Loading projects for branchId:', branchId, 'customerId:', customerId)
      const projectsData = await financeService.getProjects(branchId, customerId)
      console.log('Projects API response:', projectsData)
      setProjects(projectsData)
    } catch (error: any) {
      console.error('Error loading projects:', error)
      console.error('Error response:', error.response?.data)
      await showError('Error', 'Failed to load projects')
    }
  }

  // Handle customer selection
  const handleCustomerChange = (customerId: string) => {
    const customer = customers.find(c => c.customerId === Number(customerId))
    if (customer) {
      setSelectedCustomer(customer)
      setQuotation(prev => ({
        ...prev,
        customerId: customer.customerId,
      }))
      loadProjects(customer.customerId)
    }
  }

  // Calculate line totals
  const calculateLineTotals = (line: QuotationLine): QuotationLine => {
    // Item Price = Qty * Unit Price
    const itemPrice = (line.qty || 0) * (line.unitPrice || 0)
    
    // Discount Amount = Item Price * (Discount Percent / 100)
    const discountAmount = itemPrice * ((line.discountPercent || 0) / 100)
    
    // Taxable Amount = Item Price - Discount Amount
    const taxableAmount = itemPrice - discountAmount
    
    // Tax Amount = Taxable Amount * (Tax Percent / 100)
    const taxPercent = line.taxPercent || line.taxRate || 0
    const taxAmount = taxableAmount * (taxPercent / 100)
    
    // Line Amount = Taxable Amount + Tax Amount
    const lineAmount = taxableAmount + taxAmount

    return {
      ...line,
      itemPrice,
      discountAmount,
      taxableAmount,
      taxAmount,
      lineAmount,
    }
  }

  // Check if all required fields are filled
  const isFormValid = useMemo(() => {
    if (!quotation.customerId) return false
    if (!quotation.quotationD || quotation.quotationD.length === 0) return false
    
    const validLines = quotation.quotationD.filter((line, index) => {
      const isDeleted = deletedRowIndices.has(index)
      if (isDeleted) return false
      
      // Check required fields
      if (!line.description || line.description.trim() === '') return false
      if (!line.qty || line.qty <= 0) return false
      if (!line.taxRateId || line.taxRateId <= 0) return false
      
      return true
    })
    
    return validLines.length > 0
  }, [quotation.customerId, quotation.quotationD, deletedRowIndices])

  // Calculate summary totals
  const summaryTotals = useMemo(() => {
    const validLines = quotation.quotationD?.filter((_, index) => !deletedRowIndices.has(index)) || []
    
    const subTotal = validLines.reduce((sum, line) => sum + (line.itemPrice || 0), 0)
    const totalDiscount = validLines.reduce((sum, line) => sum + (line.discountAmount || 0), 0)
    const taxableAmount = validLines.reduce((sum, line) => sum + (line.taxableAmount || 0), 0)
    const totalTax = validLines.reduce((sum, line) => sum + (line.taxAmount || 0), 0)
    
    // Calculate retention amount from subTotal
    const retentionPercent = quotation.retentionPercent || 0
    const totalRetentionAmount = subTotal * (retentionPercent / 100)
    
    // Total = SubTotal - Discount + VAT - Retention
    const total = subTotal - totalDiscount + totalTax - totalRetentionAmount

    return {
      subTotal,
      totalDiscount,
      taxableAmount,
      totalTax,
      totalRetentionAmount,
      total,
    }
  }, [quotation.quotationD, quotation.retentionPercent, deletedRowIndices])

  // Update quotation totals
  useEffect(() => {
    setQuotation(prev => ({
      ...prev,
      subTotal: summaryTotals.subTotal,
      totalDiscount: summaryTotals.totalDiscount,
      totalTax: summaryTotals.totalTax,
      totalRetentionAmount: summaryTotals.totalRetentionAmount,
      total: summaryTotals.total,
    }))
  }, [summaryTotals])

  // Handle item row changes
  const handleLineChange = (index: number, field: keyof QuotationLine, value: any) => {
    setQuotation(prev => {
      const newLines = [...(prev.quotationD || [])]
      const updatedLine = { ...newLines[index], [field]: value }

      // If VAT changed, update tax rate
      if (field === 'taxRateId' && value) {
        const taxRate = taxRates.find(t => t.taxRateId === Number(value))
        if (taxRate) {
          updatedLine.taxRate = taxRate.taxRate || 0
          updatedLine.taxPercent = taxRate.taxPercent || taxRate.taxRate || 0
        }
      }

      // Recalculate line totals
      const calculatedLine = calculateLineTotals(updatedLine)
      newLines[index] = calculatedLine

      return { ...prev, quotationD: newLines }
    })
  }

  // Add new item row
  const handleAddRows = (count: number) => {
    setQuotation(prev => {
      const defaultTax = getDefaultTaxRate(taxRates)
      if (defaultTax) {
        console.log('Adding rows with default VAT:', defaultTax)
      } else {
        console.warn('No default tax rate found when adding rows. TaxRates:', taxRates)
      }
      const newLines = Array.from({ length: count }, (): QuotationLine => ({
        lineId: 0,
        quotationId: prev.quotationId || 0,
        itemId: 0,
        description: '',
        qty: 0,
        unitPrice: 0,
        itemPrice: 0,
        discountPercent: 0,
        discountAmount: 0,
        taxableAmount: 0,
        taxRateId: defaultTax?.taxRateId || 0,
        taxRate: defaultTax?.taxRate || 0,
        taxPercent: defaultTax?.taxRate || 0,
        taxAmount: 0,
        lineAmount: 0,
        companyId: companyId,
        branchId: branchId || 0,
      }))
      return { ...prev, quotationD: [...(prev.quotationD || []), ...newLines] }
    })
  }

  // Delete item row
  const handleDeleteRow = (index: number) => {
    const line = quotation.quotationD?.[index]
    if (line?.lineId && line.lineId > 0) {
      // Mark for deletion (existing row)
      setDeletedRowIndices(prev => {
        const newSet = new Set(prev)
        if (newSet.has(index)) {
          newSet.delete(index)
        } else {
          newSet.add(index)
        }
        return newSet
      })
    } else {
      // Remove from array (new row)
      setQuotation(prev => {
        const newLines = [...(prev.quotationD || [])]
        newLines.splice(index, 1)
        return { ...prev, quotationD: newLines }
      })
    }
  }

  // Get user IP address (logged-in user's IP, not server's IP)
  const getUserIPAddress = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch (error) {
      console.warn('Failed to get IP address from service, using fallback')
      return '0.0.0.0'
    }
  }

  // Handle save
  const handleSave = async () => {
    if (!branchId) {
      await showValidationError(t('quotations.pleaseSelectBranchFirst'))
      return
    }

    if (!quotation.customerId) {
      await showValidationError('Please select a customer')
      return
    }

    if (!quotation.quotationD || quotation.quotationD.length === 0) {
      await showValidationError('Please add at least one item')
      return
    }

    // Validate required fields in items
    const validLines = quotation.quotationD.filter((line, index) => {
      const isDeleted = deletedRowIndices.has(index)
      if (isDeleted) return false
      return !!(
        line.description?.trim() &&
        line.qty > 0 &&
        line.taxRateId &&
        line.taxRateId > 0
      )
    })

    if (validLines.length === 0) {
      await showValidationError('Please add at least one line with description, quantity, and VAT')
      return
    }

    // Check required fields for each line
    for (let i = 0; i < validLines.length; i++) {
      const line = validLines[i]
      if (!line.description || line.description.trim() === '') {
        await showValidationError(`Please enter description for item ${i + 1}`)
        return
      }
      if (!line.qty || line.qty <= 0) {
        await showValidationError(`Please enter quantity for item ${i + 1}`)
        return
      }
      if (!line.taxRateId || line.taxRateId <= 0) {
        await showValidationError(`Please select VAT for item ${i + 1}`)
        return
      }
    }

    try {
      setSaving(true)
      
      // Show loading
      showLoading(
        isEditMode ? 'Updating Quotation...' : 'Saving Quotation...',
        'Please wait while we process your request'
      )

      // Get logged-in user's IP address (not server's IP)
      const ipAddress = await getUserIPAddress()

      // Ensure branchId is always set from selected branch - get it fresh from user store
      const currentBranchId = user?.selectedBranch?.branchId
      if (!currentBranchId) {
        closeSwal()
        await showValidationError(t('quotations.branchIdRequired'))
        setSaving(false)
        return
      }

      // Log all parameters before save
      console.log('========== QUOTATION SAVE PARAMETERS ==========')
      console.log('Current Branch ID:', currentBranchId)
      console.log('User Selected Branch:', user?.selectedBranch)
      console.log('Company ID:', companyId)
      console.log('Quotation Data:', quotation)
      console.log('Is Edit Mode:', isEditMode)
      console.log('IP Address (User):', ipAddress)
      console.log('===============================================')

      // Prepare quotation lines - filter out empty lines (only save lines with actual data)
      const linesToSave: QuotationLine[] = []
      
      quotation.quotationD?.forEach((line, index) => {
        const isDeleted = deletedRowIndices.has(index)
        
        if (
          !isDeleted &&
          (!line.description?.trim() || !line.qty || line.qty <= 0 || !line.taxRateId || line.taxRateId <= 0)
        ) {
          return
        }

        // Ensure taxRate is set correctly - look up from taxRates if needed
        let taxRateValue = line.taxRate || line.taxPercent || 0
        if (line.taxRateId && line.taxRateId > 0 && taxRateValue === 0) {
          const taxRate = taxRates.find(t => t.taxRateId === line.taxRateId)
          if (taxRate) {
            taxRateValue = taxRate.taxPercent || taxRate.taxRate || 0
          }
        }

        // Recalculate line totals to ensure all values are correct
        const lineWithTaxRate = {
          ...line,
          taxRate: taxRateValue,
          taxPercent: taxRateValue,
        }
        const recalculatedLine = calculateLineTotals(lineWithTaxRate)

        const lineData: QuotationLine = {
          ...recalculatedLine,
          quotationId: quotation.quotationId || 0,
          companyId: companyId,
          branchId: currentBranchId, // Always use current branch ID
          isDeleted: isDeleted ? 'true' : 'false', // Lowercase "true" or "false" (small letters)
          taxRateId: line.taxRateId || 0,
          taxRate: taxRateValue, // Ensure taxRate is set
          taxPercent: taxRateValue, // Also set taxPercent
        }

        linesToSave.push(lineData)
      })

      // Build payload explicitly to ensure branchId is not null
      const payload: Quotation = {
        quotationId: quotation.quotationId || 0,
        quotationDate: quotation.quotationDate,
        quotationNumber: quotation.quotationNumber,
        customerId: quotation.customerId,
        dueDate: quotation.dueDate,
        taxRateId: quotation.taxRateId,
        subTotal: quotation.subTotal,
        totalTax: quotation.totalTax,
        discountPer: quotation.discountPer,
        totalDiscount: quotation.totalDiscount,
        total: quotation.total,
        note: quotation.note,
        companyId: companyId,
        branchId: currentBranchId, // Explicitly set branchId - never null
        projectId: quotation.projectId || 0,
        paymentTermId: quotation.paymentTermId || 0,
        category: quotation.category,
        bankId: quotation.bankId || 0,
        poNumber: quotation.poNumber,
        branchInvoice: quotation.branchInvoice || false,
        ipAddress: ipAddress, // Set logged-in user's IP address
        retentionPercent: quotation.retentionPercent || 0,
        totalRetentionAmount: quotation.totalRetentionAmount || 0,
        quotationD: linesToSave,
      }

      console.log('========== PAYLOAD BEING SENT ==========')
      console.log('Full Payload:', JSON.stringify(payload, null, 2))
      console.log('Branch ID in Payload:', payload.branchId)
      console.log('Branch ID Type:', typeof payload.branchId)
      console.log('Branch ID is Null?', payload.branchId === null)
      console.log('Branch ID is Undefined?', payload.branchId === undefined)
      console.log('========================================')

      if (isEditMode) {
        await quotationsService.updateQuotation(payload)
      } else {
        await quotationsService.insertQuotation(payload)
      }

      // Close loading
      closeSwal()
      
      // Show success
      await showSaveSuccess(isEditMode, isEditMode ? 'Quotation updated successfully' : 'Quotation created successfully')

      navigate('/quotations')
    } catch (error: any) {
      console.error('Error saving quotation:', error)
      console.error('Error response:', error.response)
      console.error('Error response data:', error.response?.data)
      
      // Close loading if still open
      closeSwal()
      
      // Show error
      await showSaveError(error, isEditMode)
    } finally {
      setSaving(false)
    }
  }

  // Format date for display
  const formatDateForInput = (dateString?: string) => {
    if (!dateString) return ''
    try {
      const date = new Date(dateString)
      return date.toISOString().split('T')[0]
    } catch {
      return dateString
    }
  }

  // Customer options
  const customerOptions: SearchableSelectOption[] = customers.map(customer => ({
    value: String(customer.customerId),
    label: customer.customerNameEn || customer.customerNameAr || `Customer ${customer.customerId}`,
  }))

  // Tax rate options
  const taxRateOptions: SearchableSelectOption[] = taxRates.map(tax => ({
    value: String(tax.taxRateId),
    label: `${tax.taxNameEn || tax.taxNameAr || 'Tax'} (${tax.taxPercent || tax.taxRate || 0}%)`,
  }))

  // Payment term options - show actual names (lookupNameEn/lookupNameAr), not IDs
  const paymentTermOptions: SearchableSelectOption[] = paymentTerms.map(term => ({
    value: String(term.id),
    label: term.lookupNameEn || term.lookupNameAr || term.paymentTermNameEn || term.paymentTermNameAr || String(term.id),
  }))

  // Project options - handle undefined properly
  const projectOptions: SearchableSelectOption[] = [
    // Add "None" option first
    { value: '0', label: 'None' },
    // Then add all projects (filter out the "None" project from API if id is 0)
    ...projects
      .filter(project => {
        // Get the actual ID (could be 'id' or 'projectId')
        const actualId = project.id ?? project.projectId
        // Filter out invalid projects and the "None" project (id: 0)
        return project && actualId && actualId !== 0
      })
      .map(project => {
        // Get the actual ID (could be 'id' or 'projectId')
        const actualId = project.id ?? project.projectId ?? 0
        
        // Try multiple possible field names for project name
        const projectName = project.name || // API returns 'name'
                           project.projectNameEn || 
                           project.projectNameAr || 
                           project.projectName || 
                           project.nameEn ||
                           project.nameAr ||
                           (actualId ? `Project ${actualId}` : 'Unknown Project')
        
        // Ensure we have a valid label (not undefined or empty)
        const validLabel = projectName && projectName !== 'undefined' && projectName.trim() !== '' 
          ? projectName.trim() 
          : `Project ${actualId}`
        
        console.log('Project mapping:', { 
          id: project.id,
          projectId: project.projectId,
          actualId: actualId,
          name: project.name,
          projectName: validLabel, 
          originalProject: project 
        })
        
        return {
          value: String(actualId),
          label: validLabel,
        }
      })
  ]

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title={isEditMode ? t('quotations.editQuotation') : t('quotations.newQuotation')}
        description={isEditMode ? t('quotations.updateQuotationDetails') : t('quotations.createNewQuotation')}
      />

      {/* Company and Customer Selection */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>{t('quotations.branch')}</Label>
              <Input value={user?.selectedBranch?.branchName || 'N/A'} readOnly className="bg-muted" />
            </div>
            <div className="space-y-2">
              <Label>{t('quotations.quotationToClient')}</Label>
              <SearchableSelect
                options={customerOptions}
                value={quotation.customerId ? String(quotation.customerId) : ''}
                onValueChange={handleCustomerChange}
                placeholder={t('quotations.selectCustomer')}
                searchPlaceholder={t('quotations.searchCustomers')}
                emptyMessage={t('quotations.noCustomersFound')}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Client Details (Read-only, auto-filled) */}
      {selectedCustomer && (
        <Card>
          <CardHeader>
            <CardTitle>Client Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Name En</Label>
                <Input value={selectedCustomer.customerNameEn || ''} readOnly className="bg-muted" />
              </div>
              <div className="space-y-2">
                <Label>Name Ar</Label>
                <Input value={selectedCustomer.customerNameAr || ''} readOnly className="bg-muted" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={selectedCustomer.email || ''} readOnly className="bg-muted" />
              </div>
              <div className="space-y-2">
                <Label>Phone #</Label>
                <Input value={selectedCustomer.phone || selectedCustomer.phoneNo || selectedCustomer.mobileNo || ''} readOnly className="bg-muted" />
              </div>
              <div className="space-y-2">
                <Label>Company Registration Number</Label>
                <Input value={selectedCustomer.companyRegistrationNumber || selectedCustomer.companyRegistrationNo || selectedCustomer.companyRegNumber || ''} readOnly className="bg-muted" />
              </div>
              <div className="space-y-2">
                <Label>Vat #</Label>
                <Input value={selectedCustomer.vatNumber || '0'} readOnly className="bg-muted" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quotation Section */}
      <Card>
        <CardHeader>
          <CardTitle>Quotation</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            <div className="space-y-2">
              <Label>Quotation Date</Label>
              <Input
                type="date"
                value={formatDateForInput(quotation.quotationDate)}
                onChange={(e) => setQuotation(prev => ({ ...prev, quotationDate: e.target.value }))}
                readOnly
                onClick={(e) => (e.target as HTMLInputElement).showPicker()}
                className="cursor-pointer"
              />
            </div>
            {isEditMode && (
              <div className="space-y-2">
                <Label>Quotation #</Label>
                <Input
                  value={quotation.quotationNumber || ''}
                  readOnly
                  className="bg-muted"
                  placeholder={t('quotations.autoGenerated')}
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>P.O. #</Label>
              <Input
                value={quotation.poNumber || ''}
                onChange={(e) => setQuotation(prev => ({ ...prev, poNumber: e.target.value }))}
                placeholder="Enter P.O. number"
              />
            </div>
            <div className="space-y-2">
              <Label>Project</Label>
              <SearchableSelect
                options={projectOptions}
                value={quotation.projectId ? String(quotation.projectId) : ''}
                onValueChange={(value) => {
                  console.log('Project selected, value:', value)
                  const projectId = value ? Number(value) : 0
                  console.log('Setting projectId to:', projectId)
                  setQuotation(prev => {
                    const updated = { ...prev, projectId }
                    console.log('Updated quotation:', updated)
                    return updated
                  })
                }}
                placeholder="Select project..."
                searchPlaceholder="Search projects..."
                emptyMessage="No projects found"
                disabled={!quotation.customerId}
              />
            </div>
            <div className="space-y-2">
              <Label>Payment Term</Label>
              <SearchableSelect
                options={paymentTermOptions}
                value={quotation.paymentTermId ? String(quotation.paymentTermId) : ''}
                onValueChange={(value) => {
                  const termId = Number(value) || 0
                  const selectedTerm = paymentTerms.find(term => term.id === termId)
                  const termLabel = selectedTerm?.lookupNameEn || selectedTerm?.lookupNameAr || ''
                  
                  // Calculate due date based on payment term
                  let newDueDate = ''
                  const quotationDate = quotation.quotationDate || new Date().toISOString().split('T')[0]
                  const baseDate = new Date(quotationDate)
                  
                  if (termLabel.toLowerCase() === 'advance' || termLabel.toLowerCase() === 'due on receipt') {
                    // For Advance or Due on Receipt, set due date to quotation date
                    newDueDate = quotationDate
                  } else if (termLabel.toLowerCase().startsWith('net ')) {
                    // For Net X, parse the number and add days
                    const match = termLabel.match(/net\s+(\d+)/i)
                    if (match && match[1]) {
                      const days = parseInt(match[1], 10)
                      if (!isNaN(days)) {
                        baseDate.setDate(baseDate.getDate() + days)
                        newDueDate = baseDate.toISOString().split('T')[0]
                      }
                    }
                  }
                  
                  setQuotation(prev => ({ 
                    ...prev, 
                    paymentTermId: termId,
                    dueDate: newDueDate || prev.dueDate
                  }))
                }}
                placeholder="Select payment term..."
                searchPlaceholder="Search payment terms..."
                emptyMessage="No payment terms found"
              />
            </div>
            <div className="space-y-2">
              <Label>Due Date</Label>
              <Input
                type="date"
                value={formatDateForInput(quotation.dueDate)}
                onChange={(e) => setQuotation(prev => ({ ...prev, dueDate: e.target.value }))}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Items Table */}
      <Card>
        <CardHeader className="bg-primary/5">
          <div className="flex items-center justify-between">
            <CardTitle>Items</CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => handleAddRows(1)}>
                <Plus className="w-4 h-4 mr-1" />
                Add Row
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleAddRows(3)}>
                <Plus className="w-4 h-4 mr-1" />
                Add 3 Rows
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleAddRows(5)}>
                <Plus className="w-4 h-4 mr-1" />
                Add 5 Rows
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-primary/5">
                  <th className="text-left p-3 font-semibold align-middle">DESCRIPTION <span className="text-red-500">*</span></th>
                  <th className="text-left p-3 font-semibold align-middle">QTY <span className="text-red-500">*</span></th>
                  <th className="text-left p-3 font-semibold align-middle">UNIT PRICE</th>
                  <th className="text-left p-3 font-semibold align-middle">ITEM PRICE</th>
                  <th className="text-left p-3 font-semibold align-middle">DISC. %</th>
                  <th className="text-left p-3 font-semibold align-middle">DISC. AMOUNT</th>
                  <th className="text-left p-3 font-semibold align-middle">TAXABLE</th>
                  <th className="text-left p-3 font-semibold align-middle">VAT <span className="text-red-500">*</span></th>
                  <th className="text-left p-3 font-semibold align-middle">VAT AMOUNT</th>
                  <th className="text-left p-3 font-semibold align-middle">AMOUNT</th>
                  <th className="text-left p-3 font-semibold align-middle w-20"></th>
                </tr>
              </thead>
              <tbody>
                {quotation.quotationD?.map((line, index) => {
                  const isDeleted = deletedRowIndices.has(index)
                  
                  return (
                    <tr
                      key={index}
                      className={`border-b hover:bg-gray-50/50 dark:hover:bg-gray-800/50 ${
                        isDeleted ? 'bg-red-50 dark:bg-red-900/20' : ''
                      }`}
                    >
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <Input
                            value={line.description || ''}
                            onChange={(e) => handleLineChange(index, 'description', e.target.value)}
                            placeholder="Description"
                            disabled={saving || isDeleted}
                            className="min-w-[200px]"
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <Input
                            type="number"
                            value={line.qty || ''}
                            onChange={(e) => handleLineChange(index, 'qty', Number(e.target.value) || 0)}
                            placeholder="0"
                            disabled={saving || isDeleted}
                            className="w-20"
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <Input
                            type="number"
                            step="0.01"
                            value={line.unitPrice || ''}
                            onChange={(e) => handleLineChange(index, 'unitPrice', Number(e.target.value) || 0)}
                            placeholder="0.00"
                            disabled={saving || isDeleted}
                            className="w-24"
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <Input
                            type="number"
                            step="0.01"
                            value={line.itemPrice?.toFixed(2) || '0.00'}
                            readOnly
                            className="bg-muted w-24"
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <Input
                            type="number"
                            step="0.01"
                            value={line.discountPercent || ''}
                            onChange={(e) => handleLineChange(index, 'discountPercent', Number(e.target.value) || 0)}
                            placeholder="0.00"
                            disabled={saving || isDeleted}
                            className="w-20"
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <Input
                            type="number"
                            step="0.01"
                            value={line.discountAmount?.toFixed(2) || '0.00'}
                            readOnly
                            className="bg-muted w-24"
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <Input
                            type="number"
                            step="0.01"
                            value={line.taxableAmount?.toFixed(2) || '0.00'}
                            readOnly
                            className="bg-muted w-24"
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <SearchableSelect
                            options={taxRateOptions}
                            value={line.taxRateId ? String(line.taxRateId) : ''}
                            onValueChange={(value) => handleLineChange(index, 'taxRateId', Number(value))}
                            placeholder="Select VAT"
                            searchPlaceholder="Search VAT..."
                            emptyMessage="No VAT rates found"
                            disabled={saving || isDeleted}
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <Input
                            type="number"
                            step="0.01"
                            value={line.taxAmount?.toFixed(2) || '0.00'}
                            readOnly
                            className="bg-muted w-24"
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <Input
                            type="number"
                            step="0.01"
                            value={line.lineAmount?.toFixed(2) || '0.00'}
                            readOnly
                            className="bg-muted w-24"
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteRow(index)}
                          disabled={saving}
                          className={isDeleted
                            ? "text-green-600 hover:text-green-700 hover:bg-green-50"
                            : "text-destructive hover:text-destructive hover:bg-destructive/10"
                          }
                          title={isDeleted ? "Undo delete" : "Delete row"}
                        >
                          {isDeleted ? (
                            <span className="text-xs font-semibold">Undo</span>
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Summary Section */}
          <div className="mt-6 bg-primary/5 p-4 rounded-md">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div>
                <Label className="text-sm text-muted-foreground">SUB TOTAL</Label>
                <p className="text-lg font-semibold">{summaryTotals.subTotal.toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">TOTAL DISC.</Label>
                <p className="text-lg font-semibold">{summaryTotals.totalDiscount.toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">TAXABLE AMOUNT</Label>
                <p className="text-lg font-semibold">{summaryTotals.taxableAmount.toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">TOTAL VAT</Label>
                <p className="text-lg font-semibold">{summaryTotals.totalTax.toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">RETENTION %</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={quotation.retentionPercent || ''}
                  onChange={(e) => {
                    const retentionPercent = Number(e.target.value) || 0
                    setQuotation(prev => ({ ...prev, retentionPercent }))
                  }}
                  placeholder="0"
                  disabled={saving || isPosted}
                  className="w-full mt-1"
                />
                <p className="text-sm font-semibold mt-1">{summaryTotals.totalRetentionAmount.toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">TOTAL</Label>
                <p className="text-lg font-semibold">{summaryTotals.total.toFixed(2)}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notes/Remarks */}
      <Card>
        <CardHeader>
          <CardTitle>Notes / Remarks</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={quotation.note || ''}
            onChange={(e) => setQuotation(prev => ({ ...prev, note: e.target.value }))}
            placeholder="Enter notes or remarks..."
            className="min-h-[100px]"
          />
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex flex-col sm:flex-row justify-end gap-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate('/quotations')}
          className="w-full sm:w-auto"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || !branchId || isPosted || !isFormValid}
          className="w-full sm:w-auto min-w-[150px]"
          title={
            isPosted 
              ? 'Quotation is already converted to invoice' 
              : !isFormValid 
                ? 'Please fill all required fields (Description, QTY, Unit Price, VAT) for at least one item' 
                : ''
          }
        >
          {saving ? (
            <>
              <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {isEditMode ? 'Update Quotation' : 'Save Quotation'}
            </>
          )}
        </Button>
      </div>
    </div>
  )
}
